import { StickerCard } from "@/components/sticker-card/StickerCard.tsx";
import { useSurveyFormById } from "@/queries/surveys/use-survey-forms.query.ts";
import { useSurveyDetails, useSurveyLaunch } from "@/queries/surveys/use-surveys.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiSurveyEdit, ApiSurveyStatus, SurveyStatusItems } from "@/types/survey.types.ts";
import { Button, ButtonLink, FullScreenPage, Stack, Text, Title } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface SurveyEditFinalProps {
  survey: ApiSurveyEdit;
}

export const SurveyEditFinalPage = ({ survey }: SurveyEditFinalProps) => {
  const { t } = useTranslation();

  const surveyLaunch = useSurveyLaunch();
  const { data: surveyData } = useSurveyDetails(survey.id);
  const { data: form } = useSurveyFormById(survey.id);
  const navigate = useNavigate();

  const handleLaunchSurvey = async () => {
    try {
      await surveyLaunch.mutateAsync({
        id: survey.id,
      });
      analyticsService.trackEvent(analyticEvents.surveys.launched, {
        [analyticProperties.id]: survey.id,
      });
      navigate("/surveys");
    } catch {
      // Handle error
    }
  };

  const isAbleToLaunchSurveyInstant = useMemo(() => {
    if (!surveyData) {
      return false;
    }
    return surveyData.date_starts_at == null || surveyData.date_starts_at <= new Date();
  }, [surveyData]);

  const getTitleComponent = () => {
    if (!surveyData) return null;

    if (surveyData.status !== ApiSurveyStatus.draft) {
      return (
        <Title
          size={"lg"}
          header={t("surveys:survey_has_been_updated_title")}
          caption={t("surveys:survey_has_been_updated_description")}
        ></Title>
      );
    }
    return (
      <Title
        size={"lg"}
        header={
          isAbleToLaunchSurveyInstant
            ? t("surveys:all_set_and_ready_title")
            : t("surveys:all_set_and_ready_schedule_title")
        }
        caption={
          isAbleToLaunchSurveyInstant
            ? t("surveys:all_set_and_ready_description")
            : t("surveys:all_set_and_ready_schedule_description")
        }
      ></Title>
    );
  };

  const getCTAButtons = () => {
    if (!surveyData) return null;

    if (surveyData.status !== ApiSurveyStatus.draft) {
      return (
        <ButtonLink
          type={"submit"}
          size={"lg"}
          variant={"primary"}
          href={`/surveys/${survey.id}/view/summary`}
        >
          {t("surveys:go_to_survey")}
        </ButtonLink>
      );
    }

    return (
      <>
        <Button
          type={"submit"}
          size={"lg"}
          isLoading={surveyLaunch.isPending}
          onClick={handleLaunchSurvey}
        >
          {isAbleToLaunchSurveyInstant
            ? t("surveys:launch_survey")
            : t("surveys:launch_on_schedule")}
        </Button>
        <ButtonLink type={"submit"} size={"lg"} variant={"secondary"} href={"/surveys"}>
          {t("surveys:save_as_draft")}
        </ButtonLink>
      </>
    );
  };

  return (
    <>
      <FullScreenPage isCenterVertical={true} contentSize={"md"}>
        <Stack direction={"horizontal"} items={"center"}>
          <StickerCard
            title={survey.title}
            caption={survey.description || undefined}
            emoji={survey.emoji || undefined}
            status={SurveyStatusItems().find((x) => x.id == surveyData?.status)?.title}
            contentSlot={
              <>
                {form && (
                  <Text className={"text-secondary"}>
                    {t("plurals:question_count", { count: form.stats.total_questions })}
                  </Text>
                )}

                {surveyData && (
                  <Text className={"text-secondary"}>
                    {t("plurals:employees_count", {
                      count: surveyData.participants_stats.total_count,
                    })}
                  </Text>
                )}
              </>
            }
          />

          <Stack className={"pl-18"} gap={"lg"}>
            {getTitleComponent()}

            <Stack direction={"horizontal"} gap={"sm"}>
              {getCTAButtons()}
            </Stack>
          </Stack>
        </Stack>
      </FullScreenPage>
    </>
  );
};
